/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {clearQueryParams} from '@ion/applications/src/query/state';
import {translate} from '@ion/core/src/locale/locale';
import {FilterChecklistValueBase} from '@ion/dls/src/filters/types';
import {TrackingMode} from '@ion/dls/src/types/types';
import {useFleetResetUpdates} from '@/routes/tracking2/hooks/useFleetResetUpdates';
import {
  resetTrackingAssetsState,
  setSelectedFiltersActive
} from '@/store/actions/tracking';
import {
  findNearbyIsActiveSelector,
  queryParamsSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {
  TrackingDrawerFilterOptionBase,
  TrackingDrawerFilterPropsBase,
  TrackingFavoriteFilterState
} from './types';
import {
  areTrackingFilterSelectionsEqual,
  createTrackingDrawerOptionLookup,
  createTrackingFavoriteFilterState,
  getTrackingDrawerSelectedPayloads,
  getTrackingDrawerSelectedValues,
  getTrackingFavoriteSelectedItems,
  identityTrackingDrawerSelectedValue
} from './utils';

/**
 * Tracks options, selections, and apply/clear behavior for tracking filters.
 *
 * ```typescript
 * import useTrackingDrawerFilter from './useTrackingDrawerFilter';
 *
 * ...
 *
 * const filter = useTrackingDrawerFilter({
 *   applyFilter,
 *   loadOptions,
 *   selectedItems,
 *   onSelectedCountChange
 * });
 * ```
 */
export const useTrackingDrawerFilter = ({
  applyFilter,
  filterStateKey,
  loadOptions,
  onSelectedCountChange,
  onSelectionChange,
  selectedItems = [],
  toSelectedValue = identityTrackingDrawerSelectedValue
}: Omit<TrackingDrawerFilterPropsBase, 'searchPlaceholder'>) => {
  const dispatch              = useDispatch();
  const trackingMode          = useSelector(trackingModeSelector);
  const findNearbyIsActive    = useSelector(findNearbyIsActiveSelector);
  const queryParams           = useSelector(queryParamsSelector);
  const resetFleetUpdates     = useFleetResetUpdates();
  const [options, setOptions] = useState<TrackingDrawerFilterOptionBase[]>([]);
  const appliedSelectedValues = useMemo(() => (
    getTrackingDrawerSelectedValues(selectedItems, toSelectedValue)
  ), [selectedItems, toSelectedValue]);

  const [selectedValues, setSelectedValues] = useState<FilterChecklistValueBase[]>(
    appliedSelectedValues
  );
  const [selectedPayloads, setSelectedPayloads] = useState<any[]>(selectedItems);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState<string | boolean>(false);

  const optionLookup = useMemo(() => (
    createTrackingDrawerOptionLookup(options)
  ), [options]);

  useEffect(() => {
    setSelectedValues(appliedSelectedValues);
    setSelectedPayloads(selectedItems);
  }, [appliedSelectedValues, selectedItems]);

  useEffect(() => {
    onSelectedCountChange?.(selectedValues.length);
  }, [onSelectedCountChange, selectedValues.length]);

  useEffect(() => {
    let mounted = true;
    async function loadFilterOptions() {
      setLoading(true);
      setError(false);
      try {
        const nextOptions = await loadOptions();
        if (mounted) {
          setOptions(nextOptions);
        }
      } catch (err) {
        if (mounted) {
          setError(translate('common:errorHandler.message'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }
    loadFilterOptions();
    return () => {
      mounted = false;
    };
  }, [loadOptions]);

  const applySelectedValues = useCallback((payloads: any[] = selectedPayloads) => {
    if (findNearbyIsActive) {
      dispatch(resetTrackingAssetsState() as any);
    }
    dispatch(applyFilter(payloads, trackingMode || TrackingMode.Live) as any);
    dispatch(setSelectedFiltersActive(!!payloads.length) as any);
    if (queryParams) {
      dispatch(clearQueryParams());
    }
    resetFleetUpdates();
  }, [
    applyFilter,
    dispatch,
    findNearbyIsActive,
    queryParams,
    resetFleetUpdates,
    selectedPayloads,
    trackingMode
  ]);

  const clear = useCallback(() => {
    setSelectedValues([]);
    setSelectedPayloads([]);
    onSelectionChange?.();
  }, [onSelectionChange]);

  const loadSelectedItems = useCallback((items: any[] = []) => {
    setSelectedValues(getTrackingDrawerSelectedValues(items, toSelectedValue));
    setSelectedPayloads(items);
    onSelectionChange?.();
  }, [onSelectionChange, toSelectedValue]);

  const loadFavoriteState = useCallback((state: TrackingFavoriteFilterState) => {
    loadSelectedItems(getTrackingFavoriteSelectedItems(state, filterStateKey));
  }, [filterStateKey, loadSelectedItems]);

  const getFavoriteState = useCallback(() => (
    createTrackingFavoriteFilterState(filterStateKey, selectedPayloads)
  ), [filterStateKey, selectedPayloads]);

  const discard = useCallback(() => {
    setSelectedValues(appliedSelectedValues);
    setSelectedPayloads(selectedItems);
  }, [appliedSelectedValues, selectedItems]);

  const hasChanges = useCallback(() => !areTrackingFilterSelectionsEqual(
    selectedValues,
    appliedSelectedValues
  ), [appliedSelectedValues, selectedValues]);

  const handleChange = useCallback((values: FilterChecklistValueBase[]) => {
    const payloads = getTrackingDrawerSelectedPayloads(values, optionLookup);
    setSelectedValues(values);
    setSelectedPayloads(payloads);
    onSelectionChange?.();
  }, [onSelectionChange, optionLookup]);

  return {
    applySelectedValues,
    clear,
    discard,
    error,
    getFavoriteState,
    handleChange,
    loadFavoriteState,
    loading,
    options,
    hasChanges,
    selectedValues
  };
};

export default useTrackingDrawerFilter;
